import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Stethoscope, CheckCircle2, XCircle, Lock, Unlock, ExternalLink, TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { doctorsApi, usersApi, revenueApi } from "@/lib/api";
import type { Doctor } from "@/lib/api/types";
import { fmtCurrency, fmtRelativeTime } from "@/lib/utils";

/**
 * Full doctor review: profile + credentials + earnings, with the complete
 * approval lifecycle (approve / reject-with-reason) and account lock
 * (suspend / reactivate). Mirrors the go7eight merchant KYC review flow.
 */
export function DoctorReviewDialog({
  doctor, onClose,
}: {
  doctor: Doctor | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<null | "approve" | "reject" | "lock" | "unlock">(null);

  // The list DTO is the patient-facing subset — fetch the full user record
  // (license, certificate, status, rejection reason) for the review.
  const full = useQuery({
    queryKey: ["doctor-full", doctor?.id],
    queryFn: () => usersApi.get(doctor!.id).then((r) => r.data.data),
    enabled: !!doctor,
  });
  const earnings = useQuery({
    queryKey: ["doctor-earnings", doctor?.id],
    queryFn: () => revenueApi.doctor(doctor!.id).then((r) => r.data.data),
    enabled: !!doctor,
  });

  const done = () => {
    qc.invalidateQueries({ queryKey: ["doctors"] });
    qc.invalidateQueries({ queryKey: ["doctor-full"] });
    setConfirm(null);
    onClose();
  };
  const approve = useMutation({ mutationFn: () => doctorsApi.verify(doctor!.id), onSuccess: done });
  const reject = useMutation({
    mutationFn: (reason?: string) => doctorsApi.reject(doctor!.id, reason ?? ""),
    onSuccess: done,
  });
  const setStatus = useMutation({
    mutationFn: (status: "ACTIVE" | "SUSPENDED") => usersApi.setStatus(doctor!.id, status),
    onSuccess: done,
  });

  if (!doctor) return null;
  const u = full.data ?? doctor;
  const locked = u.status === "SUSPENDED" || u.status === "INACTIVE";
  const verified = !!u.doctorVerified;

  return (
    <>
      <DetailDialog
        open={!!doctor && !confirm}
        onClose={onClose}
        icon={Stethoscope}
        title={u.name}
        subtitle={`${u.specialization ?? "Doctor"} · ${u.email}`}
        footer={
          <div className="flex w-full flex-wrap gap-2">
            {!verified && (
              <>
                <Button className="flex-1" disabled={approve.isPending} onClick={() => setConfirm("approve")}>
                  <CheckCircle2 className="size-4" /> Approve
                </Button>
                <Button variant="outline" className="flex-1 border-destructive/40 text-destructive" onClick={() => setConfirm("reject")}>
                  <XCircle className="size-4" /> Reject
                </Button>
              </>
            )}
            {locked ? (
              <Button variant="outline" className="flex-1" onClick={() => setConfirm("unlock")}>
                <Unlock className="size-4" /> Unlock account
              </Button>
            ) : (
              <Button variant="outline" className="flex-1 border-destructive/40 text-destructive" onClick={() => setConfirm("lock")}>
                <Lock className="size-4" /> Lock account
              </Button>
            )}
          </div>
        }
      >
        {/* Status strip */}
        <div className="mb-2 flex flex-wrap items-center justify-center gap-2">
          {verified ? <Badge variant="success">Verified doctor</Badge> : <Badge variant="warning">Awaiting verification</Badge>}
          {locked ? <Badge variant="destructive">Locked ({u.status})</Badge> : <Badge variant="muted">{u.status}</Badge>}
          {u.doctorRejectionReason && !verified && (
            <Badge variant="destructive">Previously rejected</Badge>
          )}
        </div>
        {u.doctorRejectionReason && !verified && (
          <p className="mb-2 rounded-lg border border-destructive/30 bg-destructive/5 p-2 text-center text-xs text-destructive">
            Last rejection: {u.doctorRejectionReason}
          </p>
        )}

        {/* Earnings summary */}
        <div className="mb-3 grid grid-cols-3 gap-2 rounded-xl border border-border bg-muted/40 p-3 text-center">
          <div>
            <p className="text-[10px] font-bold uppercase text-muted-foreground">Earnings</p>
            <p className="text-sm font-extrabold">{earnings.data ? fmtCurrency(earnings.data.totalEarnings, "TZS") : "…"}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-muted-foreground">Gross</p>
            <p className="text-sm font-extrabold">{earnings.data ? fmtCurrency(earnings.data.totalGross, "TZS") : "…"}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-muted-foreground">Commission</p>
            <p className="text-sm font-extrabold">{earnings.data ? fmtCurrency(earnings.data.commissionPaid, "TZS") : "…"}</p>
          </div>
          <p className="col-span-3 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
            <TrendingUp className="size-3" /> {earnings.data?.count ?? 0} paid consultations / sales
          </p>
        </div>

        <DetailRow label="Phone" value={u.phoneNumber ?? "—"} />
        <DetailRow label="Specialization" value={u.specialization ?? "—"} />
        <DetailRow label="License number" value={u.licenseNumber ?? "—"} />
        <DetailRow label="Qualifications" value={u.qualifications ?? "—"} />
        <DetailRow label="Experience" value={u.yearsOfExperience != null ? `${u.yearsOfExperience} years` : "—"} />
        <DetailRow label="Hospital" value={u.hospital ?? "—"} />
        <DetailRow
          label="Consultation fee"
          value={u.consultationFee != null ? fmtCurrency(u.consultationFee, "TZS") : "—"}
        />
        <DetailRow
          label="Certificate"
          value={
            u.certificateUrl ? (
              <a href={u.certificateUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary">
                Open document <ExternalLink className="size-3" />
              </a>
            ) : (
              "Not uploaded"
            )
          }
        />
        <DetailRow label="Rating" value={u.rating != null ? `${u.rating} ★` : "—"} />
        <DetailRow label="Joined" value={u.createdAt ? fmtRelativeTime(u.createdAt) : "—"} />
      </DetailDialog>

      <ConfirmDialog
        open={confirm === "approve"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={() => approve.mutate()}
        icon={CheckCircle2}
        tone="success"
        title="Approve this doctor?"
        description={`${u.name} will be marked verified and appear to patients immediately.`}
        confirmLabel="Approve doctor"
        loading={approve.isPending}
      />
      <ConfirmDialog
        open={confirm === "reject"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={(reason) => reject.mutate(reason)}
        icon={XCircle}
        tone="destructive"
        title="Reject verification?"
        description="The doctor stays unverified. The reason is stored on their profile so support can follow up."
        confirmLabel="Reject"
        loading={reject.isPending}
        withReason
        reasonRequired
        reasonLabel="Reason for rejection"
        reasonPlaceholder="e.g. License number could not be validated with the medical council…"
      />
      <ConfirmDialog
        open={confirm === "lock"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={() => setStatus.mutate("SUSPENDED")}
        icon={Lock}
        tone="destructive"
        title="Lock this account?"
        description={`${u.name} will be suspended — they can't sign in or take consultations until unlocked.`}
        confirmLabel="Lock account"
        loading={setStatus.isPending}
      />
      <ConfirmDialog
        open={confirm === "unlock"}
        onOpenChange={(v) => !v && setConfirm(null)}
        onConfirm={() => setStatus.mutate("ACTIVE")}
        icon={Unlock}
        tone="success"
        title="Unlock this account?"
        description={`${u.name} will be reactivated and can use Afya24 again.`}
        confirmLabel="Unlock"
        loading={setStatus.isPending}
      />
    </>
  );
}
